/**
 * cortex-admin の pipeline config API contract（zod schema + 型）。
 * API（apps/api/admin）と Web（apps/web/cortex-admin）が共有する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business pipeline config の一覧 / 詳細 / 手動実行 API ↔ Web 型を共有し、二重定義を排除する。
 * @graph-connects none
 */
import { z } from 'zod';
/** パイプライン種別。 @graph-connects none */
export const PipelineTypeSchema = z.enum([
    'database',
    'mongodb',
    'jobcan',
    'spreadsheet_sync',
    'drive',
]);
/** パイプラインステータス。 @graph-connects none */
export const PipelineStatusSchema = z.enum(['ACTIVE', 'DISABLED']);
/**
 * 書き込みモード。
 *
 * `@cortex/core-types` の正準 `SpreadsheetWriteMode`（'overwrite' | 'append' | 'merge'）と
 * 値集合を一致させること。pipeline_configs には backlog / commit-extractor / ac-mens-code-map 等が
 * `writeMode: 'merge'` を書き込むため、ここから 'merge' を欠落させると list/get で read 時に
 * ZodError（500）になる。
 * @graph-connects none
 */
export const WriteModeSchema = z.enum(['overwrite', 'append', 'merge']);
/** パイプライン設定。 @graph-connects none */
export const PipelineConfigSchema = z.object({
    id: z.string(),
    type: PipelineTypeSchema,
    jobName: z.string(),
    targetDb: z.string().optional(),
    status: PipelineStatusSchema,
    sqlQuery: z.string().optional(),
    // drive 種別など変更検知トリガーのパイプラインは cron を持たない（実データに欠損 doc が存在する）。
    // 必須にすると cronExpression 欠損 doc 1 件で list/get の出力検証が落ち admin が 500 になる。
    cronExpression: z.string().optional(),
    writeMode: WriteModeSchema.optional(),
    embedding: z.boolean().optional(),
    // createdBy 欠損 doc（jobcan db-access 系など）は repository の toDomain で sentinel
    // 'unknown' 補完 + lazy migration により read 時点で必ず string になる（#2398）。
    // そのため契約は必須のままで list/get は 500 しない。
    createdBy: z.string(),
    schedulerJobName: z.string().optional(),
    datasetId: z.string().optional(),
    tableId: z.string().optional(),
    formId: z.number().optional(),
    filterStatus: z.string().optional(),
    spreadsheetId: z.string().optional(),
    sheetName: z.string().optional(),
    parserConfig: z.record(z.string(), z.unknown()).optional(),
    /** 定期実行でシート末尾から読む行数（spreadsheet_sync 用）。手動実行の既定値として見せる。 */
    defaultLimitFromEnd: z.number().optional(),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().optional(),
});
/** パイプライン設定一覧レスポンス。 @graph-connects none */
export const PipelineConfigListResponseSchema = z.object({
    configs: z.array(PipelineConfigSchema),
    total: z.number(),
});
/** パイプライン手動実行レスポンス。 @graph-connects none */
export const TriggerPipelineResponseSchema = z.object({
    success: z.literal(true),
    jobName: z.string(),
    /**
     * その実行に **適用された** 読み取り行数の上書き（spreadsheet_sync のみ）。
     *
     * 指定したのに返ってこない場合は pipeline-executor 側がまだ上書きに対応していない
     * （= 既定行数で走った）ことを意味するため、呼び出し側はそれを検知できる。
     */
    limitFromEnd: z.number().optional(),
    /**
     * pipeline-executor が採番した job ID。`GET /jobs/{jobId}` で状態を追える。
     *
     * optional にしているのは api-admin と pages-cortex-admin が別スタックでデプロイされ、
     * 反映順が前後するため。web が先に出た場合に required だと parse が落ちる。
     */
    jobId: z.string().optional(),
    message: z.string(),
});
/** パイプライン設定 ID input。 @graph-connects none */
export const PipelineConfigIdInputSchema = z.object({
    id: z.string().min(1),
});
/**
 * パイプライン手動実行 input。
 *
 * `limitFromEnd` は spreadsheet_sync でその実行だけシート末尾から読む行数を上書きする。
 * 値の妥当性（整数・1 以上・対応する type か）の判定は pipeline-executor が持つので、
 * ここでは型だけを通してメッセージは API のものをそのまま見せる（入口で条件を複製すると
 * API 側を変えたときに黙ってずれる）。
 * @graph-connects none
 */
export const TriggerPipelineInputSchema = PipelineConfigIdInputSchema.extend({
    limitFromEnd: z.number().optional(),
});
//# sourceMappingURL=pipeline.js.map